// Page5.js - Quote Submission Status
'use strict';

import { logger, FIELDS } from '../utils/logger.js';

/**
 * Page5: Quote Submission Status
 * Shows loading, error, and success states for quote submission
 */
class Page5 {
  constructor(widget) {
    this.widget = widget;
    this.shadowRoot = widget.shadowRoot;
    this.config = widget.config;

    // State management
    this.state = 'loading'; // 'loading', 'success', 'error'
    this.errorMessage = '';
    this.quoteData = null;
  }

  render() {
    return `
      <!-- Page 5: Quote Submission Status -->
      <div class="page" id="page-5" style="display: block;">
        <div class="page-content">

          <!-- Loading State -->
          <div class="status-container loading-state" id="loading-state">
            <div class="loading-spinner">
              <div class="spinner"></div>
            </div>
            <h2 class="status-title">Sending Quote</h2>
            <p class="status-message">Please wait while we process your quote request...</p>
          </div>

          <!-- Success State -->
          <div class="status-container success-state" id="success-state" style="display: none;">
            <div class="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h2 class="status-title">Email Sent Successfully</h2>
            <p class="status-message">Your quote request has been submitted. We'll review your roof analysis and contact you within 24 hours with a detailed quote.</p>
            <button class="close-btn" id="success-close-btn">
              Close
            </button>
          </div>

          <!-- Error State -->
          <div class="status-container error-state" id="error-state" style="display: none;">
            <div class="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h2 class="status-title">Quote Submission Failed</h2>
            <p class="status-message" id="error-message-text">There was an error submitting your quote request. Please try again.</p>
            <div class="error-actions">
              <button class="retry-btn" id="retry-btn">
                Try Again
              </button>
              <button class="close-btn secondary" id="error-close-btn">
                Close
              </button>
            </div>
          </div>

        </div>

        <style>
          /* Page 5 Styles */
          #page-5 {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .page-content {
            width: 100%;
            max-width: 400px;
            padding: 40px 20px;
            text-align: center;
          }

          .status-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
          }

          /* Loading State */
          .loading-spinner {
            position: relative;
            width: 80px;
            height: 80px;
          }

          .spinner {
            width: 80px;
            height: 80px;
            border: 4px solid #e5e7eb;
            border-left: 4px solid #6366f1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Success State */
          .success-icon {
            width: 80px;
            height: 80px;
            background: #22c55e;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            animation: successPulse 0.6s ease-out;
          }

          .success-icon svg {
            width: 48px;
            height: 48px;
          }

          @keyframes successPulse {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          /* Error State */
          .error-icon {
            width: 80px;
            height: 80px;
            background: #ef4444;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            animation: errorShake 0.6s ease-out;
          }

          .error-icon svg {
            width: 48px;
            height: 48px;
          }

          @keyframes errorShake {
            0%, 20%, 40%, 60%, 80% {
              transform: translateX(0);
            }
            10%, 30%, 50%, 70%, 90% {
              transform: translateX(-10px);
            }
          }

          /* Typography */
          .status-title {
            font-size: 24px;
            font-weight: 700;
            color: #000000;
            margin: 0;
          }

          .status-message {
            font-size: 16px;
            color: #000000;
            line-height: 1.5;
            margin: 0;
          }

          /* Buttons */
          .close-btn {
            padding: 12px 32px;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            font-size: 16px;
            font-weight: 600;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
          }

          .close-btn:hover {
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
            transform: translateY(-2px);
          }

          .close-btn.secondary {
            background: white;
            border: 1px solid #cbd5e1;
            box-shadow: none;
            color: #000000;
          }

          .close-btn.secondary:hover {
            background: #f1f5f9;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .retry-btn {
            padding: 12px 24px;
            background: #ef4444;
            color: white;
            font-size: 16px;
            font-weight: 600;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }

          .retry-btn:hover {
            background: #dc2626;
            box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
            transform: translateY(-2px);
          }

          .error-actions {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            justify-content: center;
          }

          /* Responsive */
          @media (max-width: 480px) {
            .error-actions {
              flex-direction: column;
              width: 100%;
            }

            .retry-btn,
            .close-btn {
              width: 100%;
            }
          }
        </style>
      </div>
    `;
  }

  /**
   * Called when page is mounted/shown
   * @param {Object} data - Data passed to the page (quoteData)
   */
  onMount(data = {}) {
    this.quoteData = data.quoteData || null;

    // Start with loading state
    this.showLoadingState();

    // Setup event handlers
    this.setupEventHandlers();

    // Submit the quote
    this.submitQuote();
  }

  setupEventHandlers() {
    // Success close button - direct close without confirmation
    const successCloseBtn = this.shadowRoot.querySelector('#success-close-btn');
    if (successCloseBtn) {
      successCloseBtn.addEventListener('click', () => {
        this.widget.closeWidget(true); // true = skip confirmation
      });
    }

    // Error close button - direct close without confirmation
    const errorCloseBtn = this.shadowRoot.querySelector('#error-close-btn');
    if (errorCloseBtn) {
      errorCloseBtn.addEventListener('click', () => {
        this.widget.closeWidget(true); // true = skip confirmation
      });
    }

    // Retry button - go back to Page4
    const retryBtn = this.shadowRoot.querySelector('#retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.widget.showPage(4);
      });
    }
  }

  showLoadingState() {
    this.state = 'loading';
    this.shadowRoot.querySelector('#loading-state').style.display = 'flex';
    this.shadowRoot.querySelector('#success-state').style.display = 'none';
    this.shadowRoot.querySelector('#error-state').style.display = 'none';
  }

  showSuccessState() {
    this.state = 'success';
    this.shadowRoot.querySelector('#loading-state').style.display = 'none';
    this.shadowRoot.querySelector('#success-state').style.display = 'flex';
    this.shadowRoot.querySelector('#error-state').style.display = 'none';
  }

  showErrorState(message = 'There was an error submitting your quote request. Please try again.') {
    this.state = 'error';
    this.errorMessage = message;

    // Update error message
    const errorMessageElement = this.shadowRoot.querySelector('#error-message-text');
    if (errorMessageElement) {
      errorMessageElement.textContent = message;
    }

    this.shadowRoot.querySelector('#loading-state').style.display = 'none';
    this.shadowRoot.querySelector('#success-state').style.display = 'none';
    this.shadowRoot.querySelector('#error-state').style.display = 'flex';
  }

  async submitQuote() {
    if (!this.quoteData) {
      logger.error({
        [FIELDS.OPERATION]: 'submitQuote',
        [FIELDS.PAGE_NUMBER]: 5,
        [FIELDS.REASON]: 'no_quote_data'
      }, 'No quote data provided');
      this.showErrorState('No quote data available. Please try again.');
      return;
    }

    try {
      // Transform plugin data to server format
      const serverQuoteRequest = this.transformToServerFormat(this.quoteData);

      // Make API call to submit quote request with JWT authentication
      const response = await window.RoofScannerAPI.fetch(`${this.config.serverUrl}/api/v1/roof-scanner/submitQuote`, {
        method: 'POST',
        body: JSON.stringify(serverQuoteRequest)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }

      const result = await response.json();

      // Show success state
      this.showSuccessState();

    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'submitQuote',
        [FIELDS.PAGE_NUMBER]: 5,
        [FIELDS.ENDPOINT]: '/api/v1/roof-scanner/submitQuote'
      }, 'Error submitting quote');

      // Show error state with specific message
      let errorMessage = 'There was an error submitting your quote request. Please try again.';

      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.message.includes('Server error')) {
        errorMessage = 'Server error occurred. Please try again in a few moments.';
      }

      this.showErrorState(errorMessage);
    }
  }

  /**
   * Transform plugin data format to server-expected format
   * @param {Object} pluginData - Data from plugin
   * @returns {Object} Server-formatted quote request
   */
  transformToServerFormat(pluginData) {
    // Get selected address from session
    const selectedAddress = this.widget.getSessionData('selected_address');

    // Transform roof faces from plugin format to server format
    const roofFaces = pluginData.roofData?.faces?.map(face => {
      // Convert from metric (m²) to imperial (sq ft) if needed
      let areaInSqFt = face.actualArea || face.area || 0;

      // If area appears to be in metric (reasonable assumption for values < 50), convert to sq ft
      if (areaInSqFt < 50) {
        areaInSqFt = areaInSqFt * 10.764; // m² to sq ft conversion
      }

      return {
        area: Math.round(areaInSqFt),
        pitchDegrees: face.pitch || null,
        azimuthDegrees: face.azimuth || null
      };
    }) || [];

    // Build server request format
    const serverRequest = {
      // NOTE: organizationId is extracted from JWT token on server (security)
      // No need to send it in request body - prevents organization ID spoofing

      // Customer information
      customerName: `${pluginData.contact.firstName} ${pluginData.contact.lastName}`,
      customerEmail: pluginData.contact.email,
      customerPhone: pluginData.contact.phone,
      propertyAddress: selectedAddress?.address || 'Address not provided',

      // Material types (new format)
      currentMaterialType: pluginData.materials?.current || 'unknown',
      newMaterialType: pluginData.materials?.new || 'asphalt_shingles',

      // Roof analysis data
      roofFaces: roofFaces,

      // Metadata
      timestamp: pluginData.timestamp || new Date().toISOString()
    };

    return serverRequest;
  }

  /**
   * Called when page is unmounted/hidden
   */
  onUnmount() {
  }
}

export default Page5;